<template>
  <section id="case-details">
    <!-- SECTION A: Status reason (shown inline below status strip) -->

    <!-- Hidden inputs for copy functionality -->
    <input
      id="copy-case-id"
      type="hidden"
      :value="caseDetailsData.id"
    >
    <input
      id="copy-debtor-cvrcpr"
      type="hidden"
      :value="mainDebtor ? (mainDebtor.cvr_number || mainDebtor.cpr) : ''"
    >
    <input
      id="copy-debtor-address"
      type="hidden"
      :value="mainDebtor && mainDebtor.address ? `${mainDebtor.address.road_name} ${mainDebtor.address.house_number}, ${mainDebtor.address.zip_code} ${mainDebtor.address.city}` : ''"
    >
    <input
      id="copy-debtor-phone"
      type="hidden"
      :value="mainDebtor ? mainDebtor.phone : ''"
    >
    <input
      id="copy-debtor-email"
      type="hidden"
      :value="mainDebtor ? mainDebtor.email : ''"
    >

    <!-- SECTION D: Case Progress -->
    <b-card
      v-if="!caseProcessesCompleted && processTimelineSteps.length > 0"
      class="mb-1"
    >
      <b-card-title>{{ $t('DEBTIA_CASE_DETAIL_WHATS_HAPPENING') }}</b-card-title>
      <app-timeline>
        <app-timeline-item
          v-for="step in processTimelineSteps"
          :key="step.id"
          :variant="step.variant"
          :icon="step.icon"
        >
          <div class="d-flex flex-sm-row flex-column justify-content-between mb-25">
            <h6 class="mb-0">
              {{ step.title }}
            </h6>
            <small
              v-if="step.time"
              class="text-muted"
            >{{ step.time }}</small>
          </div>
          <p
            v-if="step.subtitle"
            class="mb-0 text-muted"
          >
            {{ step.subtitle }}
          </p>
        </app-timeline-item>
      </app-timeline>

      <!-- Primary process user action buttons -->
      <div
        v-if="caseDetailsData.status !== 2 && caseData.userActions && caseData.userActions.recipient === 'client'"
        class="mt-1 pt-1 border-top"
      >
        <p class="mb-50">
          <strong>{{ caseData.userActions.description }}</strong>
        </p>
        <div
          v-if="caseData.userActions.await"
          class="mb-50"
        >
          <b-badge variant="light-warning">
            {{ $t('DEBTIA_CASE_DETAIL_AWAITING_DAYS', { days: caseData.userActions.await_days }) }}
          </b-badge>
        </div>
        <template v-for="(data, index) in caseData.userActions.buttons">
          <b-button
            v-if="!data.await"
            :key="'btn-' + index"
            :variant="getBtnVariant(data.button_color)"
            class="mr-50 mb-50"
            @click="userActionBtnClicked(data.id, caseData.userActions.process_id, null, 1)"
          >
            {{ data.button_title }}
          </b-button>
        </template>
      </div>

      <!-- Secondary process user action buttons -->
      <div
        v-for="(userAction, index) in secondaryProcessUserActions"
        :key="'sec-' + index"
        class="mt-1 pt-1 border-top"
      >
        <p class="mb-50">
          <strong>{{ userAction.description }}</strong>
        </p>
        <b-alert
          v-if="caseStateData && caseStateData.state === 'pause'"
          variant="warning"
          show
          class="mb-50"
        >
          <div class="alert-body">
            {{ $t('DEBTIA_CASE_DETAIL_PAUSE_TEXT', { name: caseStateData.employee_name, date: caseStateData.pause_date, reason: caseStateData.description }) }}
          </div>
        </b-alert>
        <template v-for="(data, bIdx) in userAction.buttons">
          <b-button
            v-if="!data.await"
            :key="'sec-btn-' + bIdx"
            :variant="getBtnVariant(data.button_color)"
            class="mr-50 mb-50"
            @click="userActionBtnClicked(data.id, userAction.process_id, userAction, 2)"
          >
            {{ data.button_title }}
          </b-button>
        </template>
      </div>
    </b-card>

    <!-- Process Completed -->
    <b-alert
      v-if="caseProcessesCompleted"
      variant="primary"
      show
      class="mb-1"
    >
      <div class="alert-body">
        <feather-icon
          icon="CheckCircleIcon"
          class="mr-50"
        />
        {{ $t('DEBTIA_CASE_DETAIL_PROCESS_COMPLETED') }}
      </div>
    </b-alert>

    <!-- SECTION E: Status Strip + Three-column layout -->
    <div class="case-status-strip animate-fade-in">
      <button
        class="strip-back-btn"
        @click="goBack"
      >
        <feather-icon
          icon="ArrowLeftIcon"
          size="14"
        />
      </button>
      <div class="strip-divider" />
      <div class="strip-item">
        <feather-icon
          icon="BriefcaseIcon"
          size="13"
          class="strip-icon"
        />
        <span>{{ $t('DEBTIA_CASE_DETAIL_CASE_NUMBER', { id: caseDetailsData.id }) }}</span>
      </div>
      <div class="strip-divider" />
      <b-badge
        :variant="statusBadgeVariant"
        class="strip-badge"
      >
        {{ statusLabel }}
      </b-badge>
      <div
        v-if="formattedBaseType"
        class="strip-divider"
      />
      <div
        v-if="formattedBaseType"
        class="strip-item"
      >
        <feather-icon
          icon="LayersIcon"
          size="13"
          class="strip-icon"
        />
        <span>{{ formattedBaseType }}</span>
      </div>
      <div
        v-if="caseDetailsData.created_at"
        class="strip-divider"
      />
      <div
        v-if="caseDetailsData.created_at"
        class="strip-item"
      >
        <feather-icon
          icon="CalendarIcon"
          size="13"
          class="strip-icon"
        />
        <span>{{ $t('DEBTIA_CASE_DETAIL_CREATED_DATE', { date: formatDate(caseDetailsData.created_at) }) }}</span>
      </div>
    </div>

    <!-- Status reason note (afvist/opsagt) -->
    <div
      v-if="statusReasonText"
      class="status-reason-note animate-fade-in"
    >
      <feather-icon
        icon="InfoIcon"
        size="14"
        class="status-reason-icon"
      />
      <span>{{ statusReasonText }}</span>
    </div>

    <b-row class="case-main-row">
      <!-- Sagsopgørelse -->
      <b-col
        lg="4"
        class="mb-1 order-lg-2 order-2"
      >
        <div class="statements-card">
          <div class="statements-body">
            <div class="d-flex align-items-center mb-75">
              <b-avatar
                size="32"
                variant="light-primary"
                class="mr-75"
              >
                <feather-icon
                  size="15"
                  icon="FileTextIcon"
                />
              </b-avatar>
              <div>
                <h5 class="mb-0 font-weight-bold">
                  {{ $t('DEBTIA_CASE_DETAIL_STATEMENT') }}
                </h5>
                <div class="d-flex">
                  <small
                    v-if="formattedBaseType"
                    class="text-muted"
                  >
                    <feather-icon
                      size="11"
                      icon="LayersIcon"
                      class="mr-25"
                    />{{ formattedBaseType }}
                  </small>
                  <small
                    v-if="caseDetailsData.created_at"
                    class="text-muted ml-50"
                  >
                    <feather-icon
                      size="11"
                      icon="CalendarIcon"
                      class="mr-25"
                    />{{ formatDate(caseDetailsData.created_at) }}
                  </small>
                </div>
              </div>
            </div>
            <!-- Tabel med sagsopgørelse -->
            <div
              v-if="statementsData.length > 0"
              class="statements-table-wrap"
            >
              <table class="statements-table">
                <thead>
                  <tr>
                    <th class="st-col-type">
                      {{ $t('DEBTIA_COMMON_TYPE') }}
                    </th>
                    <th class="st-col-amount text-right">
                      {{ $t('DEBTIA_CASE_DETAIL_TOTAL_AMOUNT') }}
                    </th>
                    <th class="st-col-amount text-right">
                      {{ $t('DEBTIA_CASE_DETAIL_PAID_AMOUNT') }}
                    </th>
                    <th class="st-col-amount text-right">
                      {{ $t('DEBTIA_CASE_DETAIL_REMAINING_AMOUNT') }}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="(item, idx) in filteredStatements"
                    :key="'stmt-' + idx"
                    class="animate-fade-row"
                    :class="{ 'st-row-alt': idx % 2 === 0 }"
                    :style="{ animationDelay: `${idx * 0.06}s` }"
                  >
                    <td class="st-col-type">
                      {{ item.da_name || item.en_name }}
                    </td>
                    <td class="st-col-amount text-right">
                      {{ item.total_amount }}
                    </td>
                    <td class="st-col-amount text-right">
                      <span :class="item.raw_paid_amount > 0 ? 'text-success' : ''">
                        {{ item.paid_amount }}
                      </span>
                    </td>
                    <td class="st-col-amount text-right">
                      {{ item.rest_amount }}
                    </td>
                  </tr>
                </tbody>
                <tfoot>
                  <tr
                    v-if="statementsSumRow"
                    class="st-row-sum"
                  >
                    <td class="st-col-type font-weight-bold">
                      {{ $t('DEBTIA_COMMON_SUM') }}
                    </td>
                    <td class="st-col-amount text-right font-weight-bold">
                      {{ statementsSumRow.total_amount }}
                    </td>
                    <td class="st-col-amount text-right font-weight-bold text-success">
                      {{ statementsSumRow.paid_amount }}
                    </td>
                    <td class="st-col-amount text-right font-weight-bold">
                      {{ statementsSumRow.rest_amount }}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
          <!-- Opgjort restgæld -->
          <div class="restgaeld-bar restgaeld-glow">
            <div class="d-flex justify-content-between align-items-center">
              <span class="text-white font-weight-bold">
                <feather-icon
                  icon="TrendingDownIcon"
                  size="16"
                  class="mr-50"
                />
                {{ $t('DEBTIA_CASE_DETAIL_ASSESSED_DEBT') }}
              </span>
              <span class="text-white font-weight-bold restgaeld-amount">
                {{ formatAmount(animatedRestgaeld) }}
              </span>
            </div>
          </div>
        </div>
      </b-col>

      <!-- Tabs (Betalinger, Dokumenter, Sagshistorik) -->
      <b-col
        lg="5"
        class="mb-1 order-lg-1 order-3"
      >
        <b-card no-body>
          <b-tabs
            v-model="activeTab"
            pills
            card
            nav-class="nav-pills-debtia"
          >
            <!-- TAB 1: Betalinger -->
            <b-tab active>
              <template #title>
                <feather-icon
                  icon="CreditCardIcon"
                  size="16"
                  class="mr-50"
                />
                {{ $t('DEBTIA_CASE_DETAIL_TAB_PAYMENTS') }}
              </template>

              <h5>{{ $t('DEBTIA_CASE_DETAIL_DEPOSITS') }}</h5>
              <b-table
                v-if="paymentsData.length > 0"
                :fields="paymentsTableFields"
                :items="paymentsData"
                :tbody-tr-class="paymentRowClass"
                striped
                responsive
                small
                class="mb-2"
                hover
                @row-clicked="onPaymentRowClicked"
              >
                <template #row-details="row">
                  <b-card
                    class="mt-2 border-primary"
                    bg-variant="light"
                  >
                    <b-card-title class="h6 mb-2">
                      {{ $t('DEBTIA_PAYMENT_STATEMENTS_DISTRIBUTION') }}
                    </b-card-title>
                    <b-table
                      v-if="statementLinesFor(row.item).length"
                      :fields="paymentLinesFields"
                      :items="statementLinesFor(row.item)"
                      small
                      striped
                      responsive
                      class="mb-2"
                    />
                    <b-alert
                      v-else
                      variant="info"
                      show
                      class="mb-2"
                    >
                      {{ $t('DEBTIA_NO_PAYMENT_DISTRIBUTION') }}
                    </b-alert>
                    <b-button
                      variant="primary"
                      size="sm"
                      :disabled="!canDownloadFor(row.item) || pdfDownloadRowKey === rowKey(row.item)"
                      @click.stop.prevent="downloadAllocationPdf(row.item)"
                    >
                      <b-spinner
                        v-if="pdfDownloadRowKey === rowKey(row.item)"
                        small
                        class="mr-1"
                      />
                      {{ $t('DEBTIA_DOWNLOAD_PAYMENT_BREAKDOWN_PDF') }}
                    </b-button>
                  </b-card>
                </template>
              </b-table>
              <div
                v-else
                class="debtia-empty-state"
              >
                <div class="empty-state-icon">
                  <feather-icon
                    icon="CreditCardIcon"
                    size="28"
                  />
                </div>
                <p class="empty-state-text">
                  {{ $t('DEBTIA_CASE_DETAIL_NO_DEPOSITS') }}
                </p>
              </div>

              <h5 class="mt-2">
                {{ $t('DEBTIA_CASE_DETAIL_PAID_INVOICES') }}
              </h5>
              <b-table
                v-if="invoicesData.length > 0"
                :fields="invoicesTableFields"
                :items="invoicesData"
                striped
                responsive
                small
              >
                <template #cell(file_link)="data">
                  <feather-icon
                    icon="ExternalLinkIcon"
                    class="cursor-pointer text-primary"
                    @click="openLink(data.item.file_link)"
                  />
                </template>
                <template #cell(sub_total)="data">
                  {{ convertToDKK(data.item.sub_total, defaultCurrency) }}
                </template>
                <template #cell(vat_amount)="data">
                  {{ convertToDKK(data.item.vat_amount, defaultCurrency) }}
                </template>
                <template #cell(total_amount)="data">
                  {{ convertToDKK(data.item.total_amount, defaultCurrency) }}
                </template>
              </b-table>
              <div
                v-else
                class="debtia-empty-state"
              >
                <div class="empty-state-icon">
                  <feather-icon
                    icon="FileTextIcon"
                    size="28"
                  />
                </div>
                <p class="empty-state-text">
                  {{ $t('DEBTIA_CASE_DETAIL_NO_INVOICES') }}
                </p>
              </div>
            </b-tab>

            <!-- TAB 2: Dokumenter (compact) -->
            <b-tab>
              <template #title>
                <feather-icon
                  icon="FolderIcon"
                  size="16"
                  class="mr-50"
                />
                {{ $t('DEBTIA_DOCUMENTS') }}
                <b-badge
                  v-if="documentsData.length > 0"
                  pill
                  class="ml-50 doc-count-badge"
                >
                  {{ documentsData.length }}
                </b-badge>
              </template>
              <div
                v-if="documentsData.length > 0"
                class="doc-list"
              >
                <div
                  v-for="(doc, dIdx) in documentsData"
                  :key="'doc-' + dIdx"
                  class="doc-item"
                  @click="openLink(doc.file_link)"
                >
                  <div class="doc-icon-wrap">
                    <b-badge
                      :variant="getFileTypeBadgeVariant(doc.file_type)"
                      class="text-uppercase doc-type-badge"
                    >
                      {{ doc.file_type }}
                    </b-badge>
                  </div>
                  <div class="doc-info">
                    <span class="doc-title">{{ doc.title }}</span>
                    <small class="doc-meta">
                      {{ doc.category }}
                      <span
                        v-if="doc.document_send_date && doc.document_send_date !== '-'"
                        class="ml-50"
                      >
                        &middot; {{ doc.document_send_date }}
                      </span>
                      <span
                        v-if="doc.file_size && doc.file_size !== '-'"
                        class="ml-50"
                      >
                        &middot; {{ doc.file_size }}
                      </span>
                    </small>
                  </div>
                  <feather-icon
                    icon="DownloadIcon"
                    size="16"
                    class="doc-download-icon"
                  />
                </div>
              </div>
              <div
                v-else
                class="debtia-empty-state"
              >
                <div class="empty-state-icon">
                  <feather-icon
                    icon="FolderIcon"
                    size="28"
                  />
                </div>
                <p class="empty-state-text">
                  {{ $t('DEBTIA_CASE_DETAIL_NO_DOCUMENTS') }}
                </p>
              </div>
            </b-tab>

            <!-- TAB 3: Sagshistorik -->
            <b-tab>
              <template #title>
                <feather-icon
                  icon="ClockIcon"
                  size="16"
                  class="mr-50"
                />
                {{ $t('DEBTIA_CASE_DETAIL_TAB_HISTORY') }}
              </template>
              <b-form-group class="mb-1">
                <b-input-group>
                  <b-form-input
                    v-model="searchQuery"
                    :placeholder="$t('DEBTIA_CASE_DETAIL_SEARCH_HISTORY')"
                    @keyup.enter="getCaseLogsData()"
                  />
                  <b-input-group-append>
                    <b-button
                      variant="outline-primary"
                      @click="getCaseLogsData()"
                    >
                      <feather-icon icon="SearchIcon" />
                    </b-button>
                  </b-input-group-append>
                </b-input-group>
              </b-form-group>
              <app-timeline v-if="caseLogsData.length > 0">
                <app-timeline-item
                  v-for="(log, idx) in caseLogsData"
                  :key="'log-' + idx"
                  :variant="log.type === 'note' ? 'info' : 'primary'"
                >
                  <div class="d-flex justify-content-between mb-25">
                    <h6 class="mb-0">
                      {{ log.type === 'note' ? 'Note' : log.type }}
                    </h6>
                    <small class="text-muted">{{ ($i18n.locale === 'en') ? log.en_log_created_at : log.da_log_created_at }}</small>
                  </div>
                  <div
                    class="small note-content"
                    v-html="log.note"
                  />
                </app-timeline-item>
              </app-timeline>
              <div
                v-else
                class="debtia-empty-state"
              >
                <div class="empty-state-icon">
                  <feather-icon
                    icon="ClockIcon"
                    size="28"
                  />
                </div>
                <p class="empty-state-text">
                  {{ $t('DEBTIA_CASE_DETAIL_NO_HISTORY') }}
                </p>
              </div>
            </b-tab>
          </b-tabs>
        </b-card>
      </b-col>

      <!-- Right: Debtor Contact Card -->
      <b-col
        lg="3"
        class="mb-1 order-lg-3 order-1"
      >
        <!-- Skeleton placeholder while loading -->
        <div
          v-if="!mainDebtor"
          class="debtor-contact-card debtor-skeleton"
        >
          <div class="debtor-card-header">
            <div class="skeleton-avatar" />
            <div class="skeleton-line skeleton-name" />
            <div class="skeleton-line skeleton-type" />
            <div class="skeleton-badge" />
          </div>
          <div class="debtor-card-info">
            <div
              v-for="n in 4"
              :key="'skel-'+n"
              class="info-row"
            >
              <div class="skeleton-icon" />
              <div style="flex:1">
                <div class="skeleton-line skeleton-label" />
                <div class="skeleton-line skeleton-value" />
              </div>
            </div>
          </div>
        </div>

        <!-- Actual debtor card -->
        <div
          v-else
          class="debtor-contact-card"
        >
          <!-- Header: Name, type, status -->
          <div class="debtor-card-header">
            <b-avatar
              rounded
              size="48"
              class="debtor-card-avatar"
            >
              <feather-icon
                size="22"
                :icon="mainDebtor.debtor_type === 'person' ? 'UserIcon' : 'HomeIcon'"
              />
            </b-avatar>
            <h6 class="text-white mt-75 mb-0 debtor-name">
              {{ mainDebtor.name }}
            </h6>
            <small
              v-if="debtorTypeLabel"
              class="debtor-type-label"
            >
              {{ debtorTypeLabel }}
            </small>
            <b-badge
              :variant="statusBadgeVariant"
              class="mt-50"
            >
              {{ statusLabel }}
            </b-badge>
            <div class="mt-50">
              <small class="debtor-case-id">
                {{ $t('DEBTIA_CASE_DETAIL_CASE_NUMBER', { id: caseDetailsData.id }) }}
                <span
                  class="copy-btn-light ml-25"
                  @click.stop.prevent="copyCode('copy-case-id')"
                >
                  <feather-icon
                    size="12"
                    icon="CopyIcon"
                  />
                </span>
              </small>
            </div>
          </div>

          <!-- Info rows -->
          <div class="debtor-card-info">
            <!-- CVR/CPR -->
            <div class="info-row">
              <div class="info-icon">
                <feather-icon
                  icon="HashIcon"
                  size="14"
                />
              </div>
              <div>
                <small class="info-label">{{ mainDebtor.cvr_number ? 'CVR' : 'CPR' }}</small>
                <span class="info-value">
                  {{ mainDebtor.cvr_number || mainDebtor.cpr || '-' }}
                  <span
                    v-if="mainDebtor.cvr_number || mainDebtor.cpr"
                    class="copy-btn-light ml-25"
                    @click.stop.prevent="copyCode('copy-debtor-cvrcpr')"
                  >
                    <feather-icon
                      size="11"
                      icon="CopyIcon"
                    />
                  </span>
                </span>
              </div>
            </div>

            <!-- Adresse -->
            <div class="info-row">
              <div class="info-icon">
                <feather-icon
                  icon="MapPinIcon"
                  size="14"
                />
              </div>
              <div>
                <small class="info-label">{{ $t('DEBTIA_ADDRESS') }}</small>
                <span
                  v-if="mainDebtor.address && mainDebtor.address.road_name"
                  class="info-value"
                >
                  {{ mainDebtor.address.road_name }} {{ mainDebtor.address.house_number }}
                  <br>
                  <small class="info-sub">{{ mainDebtor.address.zip_code }} {{ mainDebtor.address.city }}</small>
                  <span
                    class="copy-btn-light ml-25"
                    @click.stop.prevent="copyCode('copy-debtor-address')"
                  >
                    <feather-icon
                      size="11"
                      icon="CopyIcon"
                    />
                  </span>
                </span>
                <span
                  v-else
                  class="info-value info-empty"
                >-</span>
              </div>
            </div>

            <!-- Telefon -->
            <div class="info-row">
              <div class="info-icon">
                <feather-icon
                  icon="PhoneIcon"
                  size="14"
                />
              </div>
              <div>
                <small class="info-label">{{ $t('DEBTIA_PHONE') }}</small>
                <span class="info-value">
                  {{ mainDebtor.phone || '-' }}
                  <span
                    v-if="mainDebtor.phone"
                    class="copy-btn-light ml-25"
                    @click.stop.prevent="copyCode('copy-debtor-phone')"
                  >
                    <feather-icon
                      size="11"
                      icon="CopyIcon"
                    />
                  </span>
                </span>
              </div>
            </div>

            <!-- Email -->
            <div class="info-row">
              <div class="info-icon">
                <feather-icon
                  icon="MailIcon"
                  size="14"
                />
              </div>
              <div>
                <small class="info-label">{{ $t('DEBTIA_EMAIL') }}</small>
                <span class="info-value">
                  {{ mainDebtor.email || '-' }}
                  <span
                    v-if="mainDebtor.email"
                    class="copy-btn-light ml-25"
                    @click.stop.prevent="copyCode('copy-debtor-email')"
                  >
                    <feather-icon
                      size="11"
                      icon="CopyIcon"
                    />
                  </span>
                </span>
              </div>
            </div>
          </div>

          <!-- Additional debtors -->
          <div
            v-if="otherDebtors.length > 0"
            class="debtor-card-footer"
          >
            <small class="d-block mb-25" style="color: rgba(255,255,255,0.5);">{{ $t('DEBTIA_CASE_DETAIL_OTHER_DEBTORS') }}</small>
            <small
              v-for="(debtor, idx) in otherDebtors"
              :key="'debtor-' + idx"
              style="color: rgba(255,255,255,0.8);"
            >
              {{ debtor.name }}<span
                v-if="debtor.cvr_number"
                style="color: rgba(255,255,255,0.5);"
              > ({{ debtor.cvr_number }})</span><span v-if="idx < otherDebtors.length - 1">, </span>
            </small>
          </div>
        </div>
      </b-col>
    </b-row>

    <!-- SECTION F: Seneste aktivitet -->
    <div class="case-activity-footer animate-slide-up">
      <div class="activity-header">
        <h6 class="mb-0 activity-title">
          <feather-icon
            icon="ActivityIcon"
            size="16"
            class="mr-50"
          />
          {{ $t('DEBTIA_CASE_DETAIL_LATEST_ACTIVITY') }}
        </h6>
        <a
          href="javascript:void(0)"
          class="activity-see-all"
          @click="activeTab = 2"
        >
          {{ $t('DEBTIA_COMMON_SEE_ALL') }}
          <feather-icon
            icon="ArrowRightIcon"
            size="14"
            class="ml-25"
          />
        </a>
      </div>
      <div
        v-if="caseLogsData.length > 0"
        class="activity-items"
      >
        <div
          v-for="(log, idx) in caseLogsData.slice(0, 3)"
          :key="'activity-' + idx"
            class="activity-item"
          >
            <div class="activity-dot" />
            <div class="activity-content">
              <small class="activity-time">{{ ($i18n.locale === 'en') ? log.en_log_created_at : log.da_log_created_at }}</small>
              <span
                class="activity-note"
                v-html="log.note"
              />
            </div>
          </div>
        </div>
        <div
          v-else
          class="text-muted small py-50 px-1"
        >
          {{ $t('DEBTIA_CASE_DETAIL_NO_ACTIVITY') }}
        </div>
      </div>

      <!-- User Action Reason Modal (kept from original) -->
      <user-action-reason-modal
        ref="reasonPopup"
        :btn-type="btnType"
        :clicked-btn-id="clickedBtnId"
        :process-id="processId"
        :reason-btn="reasonbtn"
        @userActionButtonClicked="userActionButtonClicked"
      />
  </section>
</template>

<script>
import {
  BRow,
  BCol,
  BCard,
  BButton,
  BTable,
  BCardTitle,
  BFormGroup,
  BFormInput,
  BInputGroup,
  BInputGroupAppend,
  BBadge,
  BAvatar,
  BAlert,
  BTabs,
  BTab,
  BSpinner,
} from 'bootstrap-vue'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import AppTimeline from '@core/components/app-timeline/AppTimeline.vue'
import AppTimelineItem from '@core/components/app-timeline/AppTimelineItem.vue'
import Ripple from 'vue-ripple-directive'
import router from '@/router'
import axios from '@axios'
import i18n from '@/libs/i18n'
import constants from '@/app-configs/constants'
import UserActionReasonModal from './userActionReasonModal.vue'

// Bootstrap-Vue row-details flag (literal name required by the library).
const SHOW_DETAILS_KEY = '_showDetails'

export default {
  components: {
    BRow,
    BCol,
    BCard,
    BButton,
    BTable,
    BCardTitle,
    BFormGroup,
    BFormInput,
    BInputGroup,
    BInputGroupAppend,
    BBadge,
    BAvatar,
    BAlert,
    BTabs,
    BTab,
    AppTimeline,
    AppTimelineItem,
    UserActionReasonModal,
    // eslint-disable-next-line vue/no-unused-components
    ToastificationContent,
    BSpinner,
  },
  directives: {
    Ripple,
  },
  data() {
    return {
      secondaryProcessUserActions: [],
      caseData: {},
      caseDetailsData: {},
      caseTaskActionData: {},
      documentsData: [],
      caseStateData: [],
      statementsData: [],
      costStatementsData: [],
      collectionFeesData: [],
      compansationFeesData: [],
      statementsAmount: [],
      paymentsData: [],
      id: 0,
      user_type: JSON.parse(localStorage.getItem('userData')).data.user_type,
      user_role: JSON.parse(localStorage.getItem('userData')).role[0],
      loader: true,
      generalLoader: false,
      isBlur: false,
      caseId: 0,
      caseLogsData: [],
      caseCustomVariables: [],
      caseStatements: [],
      perPage: 1000000,
      searchQuery: '',
      totalPages: 0,
      currentPage: 1,
      constants,
      invoicesData: [],
      caseProcessesCompleted: null,
      defaultCurrency: '',
      debt_case_state: null,
      reasonbtn: {},
      btnType: 0,
      processId: 0,
      clickedBtnId: 0,
      activeTab: 0,
      animatedRestgaeld: 0,
      expandedRowKey: null,
      pdfDownloadRowKey: null,
    }
  },
  computed: {
    mainDebtor() {
      if (!this.caseData.debtors || this.caseData.debtors.length === 0) return null
      return this.caseData.debtors.find(d => d.isMainDebtor) || this.caseData.debtors[0]
    },
    otherDebtors() {
      if (!this.caseData.debtors || this.caseData.debtors.length <= 1) return []
      const main = this.mainDebtor
      return this.caseData.debtors.filter(d => d !== main)
    },
    debtorTypeLabel() {
      if (!this.mainDebtor || !this.mainDebtor.debtor_type) return ''
      const types = {
        'capital-company': this.$t('DEBTIA_CLIENT_COMPANY'),
        'sole-proprietorship': this.$t('DEBTIA_CASES_DEBTOR_TYPE_SOLE_PROPRIETORSHIP'),
        person: this.$t('DEBTIA_CLIENT_PRIVATE_PERSON'),
      }
      return types[this.mainDebtor.debtor_type] || ''
    },
    formattedBaseType() {
      const bt = this.caseDetailsData.base_type
      if (!bt) return ''
      // base_type can be an object with .name or a string
      const name = bt.name || (typeof bt === 'string' ? bt : '')
      if (!name) return ''
      const match = name.match(/\(([^)]+)\)/)
      return match ? match[1] : name
    },
    totalPaidAmount() {
      // Betalt beløb = sum af paidAmount fra alle statement-grupper (ekskl. Sum-rækken)
      // Dette er reelle betalinger registreret i BC, IKKE paid_amount fra grundlagsdokumenter
      const sumRow = this.statementsData.find(s => s.da_name === 'Sum' || s.en_name === 'Sum')
      if (sumRow && sumRow.raw_paid_amount > 0) return sumRow.raw_paid_amount
      // Fallback: sum af individuelle statement-grupper (ekskl. Sum)
      return this.statementsData
        .filter(s => s.da_name !== 'Sum' && s.en_name !== 'Sum')
        .reduce((sum, s) => sum + (s.raw_paid_amount || 0), 0)
    },
    totalRestgaeld() {
      // Primary: Use Sum row from statements (most reliable, matches displayed data)
      const sumRow = this.statementsData.find(s => s.da_name === 'Sum' || s.en_name === 'Sum')
      if (sumRow && sumRow.raw_rest_amount) return sumRow.raw_rest_amount
      // Fallback: Use API remaining_debt_amount
      if (this.caseStateData.remaining_debt_amount) return this.caseStateData.remaining_debt_amount
      return 0
    },
    filteredStatements() {
      return this.statementsData.filter(s => {
        if (s.da_name === 'Sum' || s.en_name === 'Sum') return false
        const en = (s.en_name || '').toLowerCase()
        const da = (s.da_name || '').toLowerCase()
        if (en.includes('bailiff') || da.includes('bailiff')) return false
        return true
      })
    },
    statementsSumRow() {
      return this.statementsData.find(s => s.da_name === 'Sum' || s.en_name === 'Sum') || null
    },
    statusBadgeVariant() {
      const s = this.caseDetailsData.status
      if (s === 1) return 'light-success'
      if (s === 2) return 'light-danger'
      if (s === 3) return 'light-danger'
      if (s === 4) return 'light-warning'
      return 'light-warning'
    },
    statusLabel() {
      const s = this.caseDetailsData.status
      if (s === 1) return this.$t('DEBTIA_STATUS_ACTIVE')
      if (s === 2) return this.$t('DEBTIA_STATUS_DECLINED')
      if (s === 3) return this.$t('DEBTIA_STATUS_TERMINATED')
      if (s === 4) return this.$t('DEBTIA_STATUS_PAUSED_DETAIL')
      if (s === 0 || s === null) return this.$t('DEBTIA_STATUS_PENDING')
      return this.$t('DEBTIA_STATUS_UNKNOWN')
    },
    statusReasonText() {
      const s = this.caseDetailsData.status
      if (s !== 2 && s !== 3) return ''
      const { reason, description } = this.caseStateData || {}
      if (!reason && !description) return ''
      let text = reason || ''
      if (description) text += text ? ` - ${description}` : description
      return text
    },
    processTimelineSteps() {
      if (!this.caseData.process_cases || this.caseData.process_cases.length === 0) return []
      const steps = []
      this.caseData.process_cases.forEach(pc => {
        if (!pc.process_data) return
        const pd = pc.process_data
        if (pd.active_module && pd.active_module.module_title) {
          const daysLeft = pd.active_module.module_settings ? pd.active_module.module_settings.awaitedDays : null
          steps.push({
            id: `current-${pc.process_id}`,
            variant: 'primary',
            icon: 'ActivityIcon',
            title: pd.active_module.module_title,
            subtitle: daysLeft ? this.$t('DEBTIA_CASE_DETAIL_AWAITING_TEXT', { days: daysLeft }) : this.$t('DEBTIA_CASE_DETAIL_CLAIM_PROCESSING'),
            time: '',
          })
        }
        if (pd.next_module && pd.next_module.module_title) {
          steps.push({
            id: `next-${pc.process_id}`,
            variant: 'secondary',
            icon: 'ArrowRightIcon',
            title: this.$t('DEBTIA_CASE_DETAIL_NEXT_STEP', { title: pd.next_module.module_title }),
            subtitle: '',
            time: '',
          })
        }
      })
      return steps
    },
    compactDocumentFields() {
      return [
        {
          key: 'category', sortable: true, label: this.$t('DEBTIA_CATEGORY'), tdClass: 'text-capitalize',
        },
        { key: 'title', sortable: true, label: this.$t('DEBTIA_TITLE') },
        { key: 'file_type', sortable: true, label: this.$t('DEBTIA_CASE_DETAIL_FIELD_FILE_TYPE') },
        { key: 'file_size', sortable: true, label: this.$t('DEBTIA_CASE_DETAIL_FIELD_SIZE') },
        { key: 'document_send_date', sortable: true, label: this.$t('DEBTIA_CASE_DETAIL_FIELD_DATE') },
        { key: 'file_link', sortable: false, label: this.$t('DEBTIA_CASE_DETAIL_FIELD_FILE') },
      ]
    },
    paymentsTableFields() {
      return [
        { key: 'id', label: this.$t('DEBTIA_COL_NR') },
        { key: 'payment_date', label: this.$t('DEBTIA_CASE_DETAIL_FIELD_PAYMENT_DATE') },
        { key: 'amount', label: this.$t('DEBTIA_AMOUNT'), tdClass: 'text-right' },
        { key: 'remaining_amount', label: this.$t('DEBTIA_REMAINING'), tdClass: 'text-right' },
      ]
    },
    invoicesTableFields() {
      return [
        { key: 'file_link', sortable: false, label: this.$t('DEBTIA_CASE_DETAIL_FIELD_FILE') },
        { key: 'payment_id', label: this.$t('DEBTIA_CASE_DETAIL_FIELD_PAYMENT_ID'), tdClass: 'text-right' },
        { key: 'sub_total', label: this.$t('DEBTIA_CASE_DETAIL_FIELD_SUBTOTAL'), tdClass: 'text-right' },
        { key: 'vat_amount', sortable: true, label: this.$t('DEBTIA_CASE_DETAIL_FIELD_VAT') },
        { key: 'total_amount', label: this.$t('DEBTIA_TOTAL'), tdClass: 'text-right' },
      ]
    },
    paymentLinesFields() {
      return [
        { key: 'jobTaskNo', label: i18n.t('DEBTIA_JOB_TASK_NO') },
        { key: 'amountToUs', label: i18n.t('DEBTIA_AMOUNT_TO_US') },
        { key: 'amountToClient', label: i18n.t('DEBTIA_AMOUNT_TO_CLIENT') },
        { key: 'amountApplied', label: i18n.t('DEBTIA_AMOUNT_APPLIED') },
      ]
    },
  },
  watch: {
    totalRestgaeld: {
      handler(newVal) {
        this.animateCounter(newVal)
      },
    },
    loader: {
      immediate: true,
      handler() {
        this.$store.commit('app/SET_GLOBAL_LOADING', {
          show: this.loader || this.generalLoader,
          text: this.$t('DEBTIA_LOADING_CASE'),
        })
      },
    },
    generalLoader() {
      this.$store.commit('app/SET_GLOBAL_LOADING', {
        show: this.loader || this.generalLoader,
        text: this.$t('DEBTIA_LOADING_CASE'),
      })
    },
    searchQuery: {
      handler() {
        if (this.searchQuery.length === 0) {
          this.currentPage = 1
          this.getCaseLogsData()
        }
      },
    },
  },
  created() {
    this.getDetails()
    this.getSecondaryProcesses()
  },
  beforeDestroy() {
    this.$store.commit('app/SET_GLOBAL_LOADING', false)
  },
  methods: {
    formatAmountToDKK(amount) {
      const numericAmount = Number(amount)
      return this.convertToDKK(Number.isFinite(numericAmount) ? numericAmount : 0, this.defaultCurrency)
    },
    rowKey(item) {
      if (!item) {
        return null
      }
      if (item.type === 'Local') {
        return `local-${item.id || item.systemId}`
      }
      return `bc-${item.id || item.systemId}`
    },
    paymentRowClass(item, type) {
      if (type !== 'row' || !item || !this.expandedRowKey) {
        return null
      }
      return this.rowKey(item) === this.expandedRowKey ? 'b-table-row-selected' : null
    },
    statementLinesFor(item) {
      if (!item) return []
      if (Array.isArray(item.statementLinesFor) && item.statementLinesFor.length) {
        return item.statementLinesFor
      }
      if (!Array.isArray(item.statementBreakdown) || !item.statementBreakdown.length) {
        return []
      }
      return item.statementBreakdown.map(line => ({
        jobTaskNo: line.jobTaskNo || '-',
        amountApplied: this.formatAmountToDKK(line.amountApplied),
        amountToUs: this.formatAmountToDKK(line.amountToUs),
        amountToClient: this.formatAmountToDKK(line.amountToClient),
      }))
    },
    canDownloadFor(item) {
      return Boolean(item && item.allocationPaymentId)
    },
    onPaymentRowClicked(item) {
      const key = this.rowKey(item)
      // Collapse any other currently open row so only the clicked payment shows its allocation panel.
      this.paymentsData.forEach(row => {
        if (row !== item && row[SHOW_DETAILS_KEY]) {
          this.$set(row, SHOW_DETAILS_KEY, false)
        }
      })
      const next = !item[SHOW_DETAILS_KEY]
      this.$set(item, SHOW_DETAILS_KEY, next)
      this.expandedRowKey = next ? key : null
    },
    async fetchInitialData() {
      await this.getPayments()
    },
    goBack() {
      // If there's browser history, go back (preserves Cases.vue query params)
      if (window.history.length > 1) {
        this.$router.back()
      } else {
        this.$router.push({ name: 'cases' })
      }
    },
    formatAmount(amount) {
      if (!amount && amount !== 0) return '0,00 kr.'
      return new Intl.NumberFormat('da-DK', {
        style: 'currency',
        currency: this.defaultCurrency || 'DKK',
        minimumFractionDigits: 2,
      }).format(amount)
    },
    formatDate(dateStr) {
      if (!dateStr) return '-'
      return new Date(dateStr).toLocaleDateString('da-DK')
    },
    animateCounter(target) {
      if (!target || target <= 0) {
        this.animatedRestgaeld = 0
        return
      }
      const duration = 1500
      const startTime = performance.now()
      const animate = currentTime => {
        const elapsed = currentTime - startTime
        const progress = Math.min(elapsed / duration, 1)
        // Ease out cubic
        const eased = 1 - (1 - progress) ** 3
        this.animatedRestgaeld = target * eased
        if (progress < 1) {
          requestAnimationFrame(animate)
        } else {
          this.animatedRestgaeld = target
        }
      }
      requestAnimationFrame(animate)
    },
    formatFileSize(bytes) {
      if (!bytes || bytes === '-') return '-'
      const num = parseInt(bytes, 10)
      if (Number.isNaN(num) || num === 0) return '-'
      const k = 1024
      const sizes = ['B', 'KB', 'MB', 'GB']
      const i = Math.floor(Math.log(num) / Math.log(k))
      return `${parseFloat((num / (k ** i)).toFixed(1))} ${sizes[i]}`
    },
    fetchFileSizes() {
      this.documentsData.forEach((doc, index) => {
        if (!doc.file_link) return
        const xhr = new XMLHttpRequest()
        xhr.open('HEAD', doc.file_link, true)
        xhr.onreadystatechange = () => {
          if (xhr.readyState === 4 && xhr.status === 200) {
            const size = xhr.getResponseHeader('Content-Length')
            if (size) {
              this.$set(this.documentsData[index], 'file_size', this.formatFileSize(size))
            }
          }
        }
        xhr.send()
      })
    },
    getFileTypeBadgeVariant(fileType) {
      if (!fileType) return 'light-secondary'
      const type = fileType.toUpperCase()
      if (type === 'PDF') return 'light-danger'
      if (['JPG', 'JPEG', 'PNG', 'GIF', 'WEBP'].includes(type)) return 'light-success'
      if (['DOC', 'DOCX'].includes(type)) return 'light-primary'
      if (['XLS', 'XLSX', 'CSV'].includes(type)) return 'light-warning'
      return 'light-secondary'
    },
    getBtnVariant(variant) {
      return variant.includes('#') ? 'relief-primary' : `relief-${variant.toLowerCase()}`
    },
    async getSecondaryProcesses() {
      this.secondaryProcessUserActions = []
      const caseID = this.caseId || router.currentRoute.params.id
      const url = `/secondary-process-data/${caseID}`
      await axios.get(url).then(response => {
        if (response.status === 200) {
          const { process_cases } = response.data.data
          process_cases.forEach(element => {
            if (element.process_data.userActions.recipient === 'client' && element.process_data.active_module.module_id === 9) {
              this.secondaryProcessUserActions.push(element.process_data.userActions)
            }
          })
        }
      }).catch(() => {})
    },
    userActionBtnClicked(id, processId, userActionObject, btnType) {
      let reasonbtn = {}
      if (userActionObject) {
        reasonbtn = userActionObject.buttons.find(item => item.id === id)
      } else {
        reasonbtn = this.caseData.userActions.buttons.find(item => item.id === id)
      }
      this.reasonbtn = reasonbtn
      this.processId = processId
      this.clickedBtnId = reasonbtn.id
      this.btnType = btnType
      if (reasonbtn.reason) {
        setTimeout(() => {
          this.$refs.reasonPopup.open()
        }, 100)
      } else {
        this.userActionButtonClicked(id, processId, 0, '', btnType)
      }
    },
    userActionButtonClicked(buttonId, processId, reason, reasonDescription, type) {
      this.generalLoader = true
      const caseId = this.$route.params.id
      const form = new FormData()
      form.append('process_id', processId)
      form.append('button_id', buttonId)
      form.append('case_id', caseId)
      form.append('reason', reason)
      form.append('reason_description', reasonDescription)
      const url = '/run-button-module'
      axios.post(url, form).then(response => {
        if (response.status === 200) {
          this.$toast({
            component: ToastificationContent,
            position: 'top-right',
            props: {
              title: this.$t('DEBTIA_COMMON_SUCCESS'),
              icon: 'CoffeeIcon',
              variant: 'success',
              text: this.$t('DEBTIA_TOAST_ACTION_PERFORMED'),
            },
          })
          if (type === 1) this.getDetails()
          if (type === 2) {
            this.getSecondaryProcesses()
            this.generalLoader = false
          }
        }
      }).catch(e => {
        this.$toast({
          component: ToastificationContent,
          position: 'top-right',
          props: {
            title: this.$t('DEBTIA_TOAST_FAILED'),
            icon: 'CoffeeIcon',
            variant: 'danger',
            text: this.$t(e.response.data.message[0]),
          },
        })
        this.generalLoader = false
      })
    },
    copyCode(id) {
      const codeToCopy = document.querySelector(`#${id}`)
      codeToCopy.setAttribute('type', 'text')
      codeToCopy.select()
      try {
        document.execCommand('copy')
        this.$toast({
          component: ToastificationContent,
          props: { title: this.$t('DEBTIA_TOAST_COPIED'), icon: 'CheckIcon' },
        })
      } catch (err) {
        this.$toast({
          component: ToastificationContent,
          props: { title: this.$t('DEBTIA_TOAST_COPY_FAILED'), icon: 'AlertTriangleIcon' },
        })
      }
      codeToCopy.setAttribute('type', 'hidden')
      window.getSelection().removeAllRanges()
    },
    async openLink(href) {
      window.open(href, '_blank')
    },
    convertToDKK(amount, currency) {
      return new Intl.NumberFormat('da-DK', { style: 'currency', currency: currency || 'DKK' }).format(parseFloat(amount).toFixed(2))
    },
    async getDetails() {
      this.caseId = router.currentRoute.params.id
      const caseDetails = await axios.get(`${constants.baseUrl}/get-debt-case/${this.caseId}`).catch(error => {
        if (error.response && error.response.status === 404) {
          this.$router.push('/cases')
        }
      })
      if (caseDetails && caseDetails.status) {
        const processCase = caseDetails.data.data.process_cases
        this.caseProcessesCompleted = processCase.length > 0 ? processCase[0].completed : 0
        if (caseDetails.data.data.debt_case_state !== null && typeof caseDetails.data.data.debt_case_state !== 'undefined') {
          this.caseStateData = caseDetails.data.data.debt_case_state
          this.state = caseDetails.data.data.debt_case_state.state
        } else {
          this.caseStateData = { debt_case_id: this.caseId }
        }
        if (caseDetails.data.data !== null) {
          this.debt_case_state = caseDetails.data.data.debt_case_state
          this.caseData = { ...caseDetails.data.data }
          this.caseCustomVariables = this.caseData.custom_vars
          this.caseDetailsData = this.caseData.debt_case
          if (this.caseDetailsData.status === 2 || this.caseDetailsData.status === 3) {
            this.isBlur = true
          }
          this.caseTaskActionData = this.caseData.task_action
          this.caseStateData.creditor_id = (caseDetails.data.data.client) ? caseDetails.data.data.client.id : 0
          this.caseStateData.remaining_debt_amount = (caseDetails.data.data.remainingDebtAmount) ? caseDetails.data.data.remainingDebtAmount : 0
          this.caseLogsData = caseDetails.data.data.get_logs
          this.caseStatements = caseDetails.data.data.statements
          this.invoicesData = caseDetails.data.data.invoices
          const headingData = Object.keys(caseDetails.data.data)
          let newObj = null
          const paymentDocumentsData = []
          this.statementsData = []
          this.documentsData = []
          this.costStatementsData = []
          this.statementsAmount = []
          const defaultCurrency = typeof caseDetails.data.data.debt_case.default_currency !== 'undefined' ? caseDetails.data.data.debt_case.default_currency : 'DKK'
          this.defaultCurrency = defaultCurrency
          headingData.forEach(element => {
            if (element === 'base_documents' || element === 'credit_notes' || element === 'debt_reminders' || element === 'other_files') {
              newObj = caseDetails.data.data[element]
              newObj.forEach(key => {
                if (typeof key.file_link !== 'undefined') {
                  const objArr = []
                  objArr.id = key.id
                  // Kategori med dansk label
                  const categoryLabels = {
                    base_documents: this.$t('DEBTIA_CASE_DETAIL_CAT_BASE'),
                    credit_notes: this.$t('DEBTIA_CASE_DETAIL_CAT_CREDIT'),
                    debt_reminders: this.$t('DEBTIA_CASE_DETAIL_CAT_REMINDER'),
                    other_files: this.$t('DEBTIA_CASE_DETAIL_CAT_OTHER'),
                  }
                  objArr.category = (key.category === '' || typeof key.category === 'undefined') ? (categoryLabels[element] || element.replace('_', ' ')) : key.category
                  objArr.file_link = key.file_link
                  objArr.title = (key.title) ? key.title : '-'
                  // Filtype fra URL
                  const fileExt = key.file_link ? key.file_link.split('.').pop().split('?')[0].toUpperCase() : '-'
                  objArr.file_type = fileExt || '-'
                  objArr.file_size = '-'
                  objArr.payment_amount = (key.payment_amount) ? new Intl.NumberFormat('da-DK', { style: 'currency', currency: defaultCurrency }).format(parseFloat(key.payment_amount).toFixed(2)) : '-'
                  objArr.vat_amount = (key.vat_amount) ? new Intl.NumberFormat('da-DK', { style: 'currency', currency: defaultCurrency }).format(parseFloat(key.vat_amount).toFixed(2)) : '-'
                  objArr.paid_amount = (key.paid_amount) ? new Intl.NumberFormat('da-DK', { style: 'currency', currency: defaultCurrency }).format(parseFloat(key.paid_amount).toFixed(2)) : '-'
                  objArr.interest_rate_percentage = key.interest_rate_percentage
                  objArr.interest_rate_agreed = key.interest_rate_agreed
                  objArr.document_due_date = (key.document_due_date) ? key.document_due_date : '-'
                  objArr.document_send_date = (key.document_send_date) ? key.document_send_date : '-'
                  if (element === 'credit_notes') {
                    objArr.payment_amount = new Intl.NumberFormat('da-DK', { style: 'currency', currency: defaultCurrency }).format(parseFloat(key.credit_note_amount).toFixed(2))
                  }
                  if (element === 'debt_reminders') {
                    objArr.payment_amount = new Intl.NumberFormat('da-DK', { style: 'currency', currency: defaultCurrency }).format((parseFloat(key.compensation_fee) + parseFloat(key.reminder_fee)).toFixed(2))
                  }
                  this.documentsData.push(objArr)
                }
              })
            }
            if (element === 'statements') {
              const statementsKeysRow = []
              const statementGroups = caseDetails.data.data[element]
              statementGroups.forEach(statementGroupValue => {
                const newGroup = {}
                newGroup.en_name = statementGroupValue.en_name
                newGroup.da_name = statementGroupValue.da_name
                newGroup.category = (i18n.locale === 'en') ? statementGroupValue.en_name : statementGroupValue.da_name
                newGroup.total_amount = new Intl.NumberFormat('da-DK', { style: 'currency', currency: defaultCurrency }).format(parseFloat(statementGroupValue.totalAmount).toFixed(2))
                newGroup.paid_amount = new Intl.NumberFormat('da-DK', { style: 'currency', currency: defaultCurrency }).format(parseFloat(statementGroupValue.paidAmount).toFixed(2))
                newGroup.rest_amount = new Intl.NumberFormat('da-DK', { style: 'currency', currency: defaultCurrency }).format(parseFloat(statementGroupValue.restAmount).toFixed(2))
                newGroup.raw_rest_amount = parseFloat(statementGroupValue.restAmount) || 0
                newGroup.raw_total_amount = parseFloat(statementGroupValue.totalAmount) || 0
                newGroup.raw_paid_amount = parseFloat(statementGroupValue.paidAmount) || 0
                if (typeof statementGroupValue.statements !== 'undefined') {
                  Object.values(statementGroupValue.statements).forEach(key => {
                    if (caseDetails.data.data.statementKeys.includes(key.jobTaskNo)) {
                      const objArr = {}
                      objArr.en_name = key.en_name
                      objArr.da_name = key.da_name
                      objArr.category = (i18n.locale === 'en') ? key.en_name : key.da_name
                      objArr.jobTaskNo = key.jobTaskNo
                      objArr.total_amount = new Intl.NumberFormat('da-DK', { style: 'currency', currency: defaultCurrency }).format(parseFloat(key.scheduleTotalPrice).toFixed(2))
                      objArr.paid_amount = new Intl.NumberFormat('da-DK', { style: 'currency', currency: defaultCurrency }).format(parseFloat(key.paidAmount).toFixed(2))
                      objArr.rest_amount = new Intl.NumberFormat('da-DK', { style: 'currency', currency: defaultCurrency }).format(parseFloat(key.scheduleTotalPrice - key.paidAmount).toFixed(2))
                      paymentDocumentsData.push(objArr)
                      if (!statementsKeysRow.includes(key.jobTaskNo)) {
                        statementsKeysRow.push(key.jobTaskNo)
                        this.statementsAmount.push(key)
                      }
                    }
                  })
                } else if (statementGroupValue.en_name !== 'Sum' || statementGroupValue.da_name !== 'Sum') {
                  const defaultStatementLine = {}
                  defaultStatementLine.en_name = statementGroupValue.en_name
                  defaultStatementLine.da_name = statementGroupValue.da_name
                  defaultStatementLine.jobTaskNo = statementGroupValue.jobTaskNo
                  defaultStatementLine.scheduleTotalPrice = statementGroupValue.totalAmount
                  defaultStatementLine.paidAmount = statementGroupValue.paidAmount
                  this.statementsAmount.push(defaultStatementLine)
                }
                // Skjul Bailiff proceedings - må aldrig vises
                const enLower = (newGroup.en_name || '').toLowerCase()
                const daLower = (newGroup.da_name || '').toLowerCase()
                if (enLower.includes('bailiff') || daLower.includes('bailiff')) {
                  return
                }
                this.statementsData.push(newGroup)
              })
            }
            if (element === 'payments') {
              newObj = caseDetails.data.data[element]
              newObj.forEach(key => {
                const objArr = {}
                objArr.id = key.entryNo
                objArr.payment_date = key.dueDate
                objArr.amount = new Intl.NumberFormat('da-DK', { style: 'currency', currency: defaultCurrency }).format(parseFloat(key.Amount).toFixed(2))
                objArr.remaining_amount = new Intl.NumberFormat('da-DK', { style: 'currency', currency: defaultCurrency }).format(parseFloat(key.remainingAmount).toFixed(2))
                objArr.casePaymentId = key.casePaymentId || key.paymentIDNo
                objArr.casePaymentEntryNo = key.casePaymentEntryNo
                objArr.allocationPaymentId = key.casePaymentEntryNo || key.entryNo || key.paymentIDNo
                objArr.statementBreakdown = Array.isArray(key.statementBreakdown) ? key.statementBreakdown : []
                objArr.statementLinesFor = objArr.statementBreakdown.map(line => ({
                  jobTaskNo: line.jobTaskNo || '-',
                  amountApplied: this.formatAmountToDKK(line.amountApplied),
                  amountToUs: this.formatAmountToDKK(line.amountToUs),
                  amountToClient: this.formatAmountToDKK(line.amountToClient),
                }))
                objArr.documents = paymentDocumentsData
                this.paymentsData.push(objArr)
              })
            }
          })
          // Hent filstørrelser asynkront efter dokumenter er indlæst
          if (this.documentsData.length > 0) {
            this.fetchFileSizes()
          }
        }
        this.loader = false
        this.generalLoader = false
      }
    },
    async getCaseLogsData() {
      this.loader = true
      const url = `${constants.serverBaseUrl}/api/v2/debtiamit/debt-case/logs-data/${this.caseId}?perPage=${this.perPage}&search=${this.searchQuery}&page=${this.currentPage}`
      await axios.get(url).then(response => {
        if (response.status === 200) {
          this.loader = false
          this.caseLogsData = response.data.data
        }
      })
    },
    async downloadAllocationPdf(item) {
      if (!this.canDownloadFor(item)) {
        return
      }
      // const paymentId = item.casePaymentId
      this.pdfDownloadRowKey = this.rowKey(item)
      const url = `${constants.baseUrl}/debt-case/download-case-payment-pdf/${this.caseId}?paymentId=${item.casePaymentId}`
      try {
        const response = await axios.get(url, { responseType: 'blob' })
        const disposition = response.headers && (response.headers['content-disposition'] || response.headers['Content-Disposition'])
        let fileName = `payment-allocation-${this.caseId}.pdf`
        if (disposition && disposition.indexOf('filename=') !== -1) {
          const [, extractedFileName] = disposition.match(/filename="?([^";]+)"?/i) || []
          if (extractedFileName) {
            fileName = extractedFileName
          }
        }
        const blob = new Blob([response.data], { type: 'application/pdf' })
        const link = document.createElement('a')
        link.href = window.URL.createObjectURL(blob)
        link.download = fileName
        document.body.appendChild(link)
        link.click()
        link.remove()
        window.URL.revokeObjectURL(link.href)
      } catch (e) {
        let message = e.message || 'PDF download failed'
        if (e.response && e.response.data instanceof Blob) {
          try {
            const text = await e.response.data.text()
            const parsed = JSON.parse(text)
            if (parsed.message) {
              message = parsed.message
            }
          } catch (ignore) {
            message = 'PDF download failed'
          }
        }
        this.$toast({
          component: ToastificationContent,
          position: 'top-right',
          props: {
            title: 'PDF',
            icon: 'AlertCircleIcon',
            variant: 'danger',
            text: message,
          },
        })
      } finally {
        this.pdfDownloadRowKey = null
      }
    },
  },
}
</script>

<style lang="scss" scoped>
$debtia-green: #088c76;
$debtia-blue: #002954;
$debtia-blue-light: #0c3860;

// ==============================
// ANIMATIONS
// ==============================
@keyframes fadeSlideUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes fadeSlideRight {
  from {
    opacity: 0;
    transform: translateX(-12px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

@keyframes glowPulse {
  0%, 100% {
    box-shadow: 0 0 8px rgba(8, 140, 118, 0.3), 0 0 20px rgba(8, 140, 118, 0.1);
  }
  50% {
    box-shadow: 0 0 16px rgba(8, 140, 118, 0.5), 0 0 40px rgba(8, 140, 118, 0.2);
  }
}

.animate-fade-in {
  animation: fadeIn 0.5s ease-out forwards;
}

.animate-slide-up {
  animation: fadeSlideUp 0.5s ease-out forwards;
  opacity: 0;
  animation-delay: 0.3s;
}

.animate-fade-row {
  animation: fadeSlideRight 0.4s ease-out forwards;
  opacity: 0;
}

// ==============================
// STATUS STRIP
// ==============================
.case-status-strip {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.6rem 1.25rem;
  margin-bottom: var(--debtia-spacing);
  background: rgba(255, 255, 255, 0.5);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 0.75rem;
  font-size: 0.82rem;
  color: #3b4a5e;
  flex-wrap: wrap;

  .strip-item {
    display: flex;
    align-items: center;
    gap: 0.35rem;
  }

  .strip-back-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    border: none;
    border-radius: 0.4rem;
    background: rgba(8, 140, 118, 0.08);
    color: $debtia-green;
    cursor: pointer;
    transition: all 0.2s ease;
    padding: 0;
    flex-shrink: 0;

    &:hover {
      background: rgba(8, 140, 118, 0.15);
      transform: translateX(-2px);
    }
  }

  .strip-icon {
    color: $debtia-green;
    opacity: 0.7;
  }

  .strip-divider {
    width: 1px;
    height: 14px;
    background: rgba(0, 0, 0, 0.15);
  }

  .strip-badge {
    font-size: 0.75rem;
  }
}

// ==============================
// STATUS REASON NOTE
// ==============================
.status-reason-note {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.55rem 1.25rem;
  margin-top: calc(var(--debtia-spacing) * -1 + 0.25rem);
  margin-bottom: var(--debtia-spacing);
  background: rgba(234, 84, 85, 0.08);
  border: 1px solid rgba(234, 84, 85, 0.2);
  border-radius: 0.6rem;
  font-size: 0.82rem;
  color: #7a3b3c;

  .status-reason-icon {
    color: #ea5455;
    flex-shrink: 0;
  }
}

// ==============================
// DEBTOR CONTACT CARD - matches sidebar gradient exactly
// ==============================
.debtor-contact-card {
  position: relative;
  background:
    radial-gradient(ellipse 150% 80% at 50% 0%, $debtia-green 0%, transparent 50%),
    radial-gradient(ellipse 180% 90% at 0% 50%, $debtia-blue 0%, transparent 50%),
    radial-gradient(ellipse 100% 50% at 0% 100%, $debtia-blue-light 0%, transparent 50%),
    #000000;
  border-radius: 1rem;
  overflow: hidden;
  box-shadow: 0 8px 32px rgba(0, 41, 84, 0.3);
  display: flex;
  flex-direction: column;
  border: 1px solid rgba(255, 255, 255, 0.1);
  animation: fadeSlideUp 0.4s ease-out both;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-image: var(--debtia-noise-texture);
    background-size: var(--debtia-noise-size);
    opacity: 0.2;
    pointer-events: none;
    z-index: 0;
  }
}

.debtor-card-header {
  position: relative;
  z-index: 1;
  padding: 1.5rem 1.5rem 1rem;
  text-align: center;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);

  .debtor-card-avatar {
    background: rgba(255, 255, 255, 0.15) !important;
    color: #fff !important;
  }
}

.debtor-name {
  font-size: 0.9rem;
  font-weight: 700;
  line-height: 1.3;
}

.debtor-type-label {
  display: block;
  color: rgba(255, 255, 255, 0.6);
  font-size: 0.75rem;
  margin-top: 0.15rem;
}

.debtor-case-id {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  color: rgba(255, 255, 255, 0.85);
  font-weight: 400;
  background: rgba(255, 255, 255, 0.1);
  padding: 0.2rem 0.6rem;
  border-radius: 0.4rem;
  font-size: 0.78rem;
  letter-spacing: 0.02em;
}

.debtor-card-info {
  position: relative;
  z-index: 1;
  padding: 0.5rem 1.5rem;
  flex-grow: 1;

  .info-row {
    display: flex;
    align-items: flex-start;
    padding: 0.7rem 0;
    border-bottom: 1px solid rgba(255, 255, 255, 0.08);
    gap: 0.75rem;

    &:last-child {
      border-bottom: none;
    }
  }

  .info-icon {
    color: rgba(255, 255, 255, 0.6);
    flex-shrink: 0;
    width: 30px;
    height: 30px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(255, 255, 255, 0.08);
    border-radius: 0.4rem;
    margin-top: 0.1rem;
  }

  .info-label {
    display: block;
    color: rgba(255, 255, 255, 0.45);
    font-size: 0.7rem;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    line-height: 1;
    margin-bottom: 0.2rem;
  }

  .info-value {
    display: block;
    color: rgba(255, 255, 255, 0.95);
    font-weight: 400;
    font-size: 0.85rem;
    line-height: 1.3;
  }

  .info-sub {
    color: rgba(255, 255, 255, 0.9);
  }

  .info-empty {
    color: rgba(255, 255, 255, 0.3);
  }
}

.debtor-card-footer {
  position: relative;
  z-index: 1;
  padding: 0.75rem 1.5rem;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(0, 0, 0, 0.1);
}

.copy-btn-light {
  cursor: pointer;
  color: rgba(255, 255, 255, 0.3);
  transition: color 0.15s;

  &:hover {
    color: #fff;
  }
}

// ==============================
// STATEMENTS CARD
// ==============================
.statements-card {
  background: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 1rem;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.08);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  animation: fadeSlideUp 0.5s ease-out forwards;
  animation-delay: 0.05s;
  opacity: 0;
}

.statements-body {
  padding: 1.25rem;
  flex-grow: 1;
}

// Statements table
.statements-table-wrap {
  overflow-x: auto;
  margin: 0 -0.25rem;
}

.statements-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  font-size: 0.82rem;

  thead th {
    font-size: 0.7rem;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: #7a8b9e;
    font-weight: 700;
    padding: 0.5rem 0.6rem;
    border-bottom: 2px solid rgba(8, 140, 118, 0.15);
    white-space: nowrap;
  }

  tbody tr {
    transition: background 0.2s ease;

    &:hover {
      background: rgba(8, 140, 118, 0.08) !important;
    }

    td {
      padding: 0.5rem 0.6rem;
      border-bottom: 1px solid rgba(0, 0, 0, 0.04);
      white-space: nowrap;
    }
  }

  .st-row-alt td {
    background: rgba(8, 140, 118, 0.03);
  }

  tfoot .st-row-sum td {
    padding: 0.65rem 0.6rem;
    border-top: 2px solid rgba(8, 140, 118, 0.2);
    border-bottom: none;
    background: rgba(8, 140, 118, 0.06);
  }

  .st-col-type {
    color: $debtia-green;
  }

  .st-col-amount {
    font-variant-numeric: tabular-nums;
  }
}

// Restgæld bar with glow
.restgaeld-bar {
  background: linear-gradient(135deg, $debtia-green 0%, $debtia-blue 100%);
  padding: 0.85rem 1.25rem;
  margin-top: auto;
  position: relative;
}

.restgaeld-glow {
  animation: glowPulse 3s ease-in-out infinite;
}

.restgaeld-amount {
  font-size: 1.15rem;
  letter-spacing: 0.01em;
}

// ==============================
// EMPTY STATES
// ==============================
.debtia-empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem 1rem;
  border: 2px dashed rgba(8, 140, 118, 0.2);
  border-radius: 0.75rem;
  background: rgba(8, 140, 118, 0.02);
  text-align: center;

  .empty-state-icon {
    width: 56px;
    height: 56px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    background: rgba(8, 140, 118, 0.08);
    color: $debtia-green;
    margin-bottom: 0.75rem;
    opacity: 0.6;
  }

  .empty-state-text {
    color: #7a8b9e;
    font-size: 0.875rem;
    margin-bottom: 0;
  }
}

// ==============================
// ACTIVITY FOOTER
// ==============================
.case-activity-footer {
  background: rgba(255, 255, 255, 0.5);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 1rem;
  padding: 1rem 1.25rem;
  margin-top: var(--debtia-spacing);

  .activity-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 0.75rem;
  }

  .activity-title {
    font-weight: 700;
    display: flex;
    align-items: center;
  }

  .activity-see-all {
    font-size: 0.82rem;
    color: $debtia-green;
    display: flex;
    align-items: center;
    text-decoration: none;
    transition: color 0.15s;

    &:hover {
      color: darken($debtia-green, 8%);
    }
  }

  .activity-items {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  .activity-item {
    display: flex;
    align-items: flex-start;
    gap: 0.75rem;
    padding: 0.5rem 0;
    border-bottom: 1px solid rgba(0, 0, 0, 0.06);

    &:last-child {
      border-bottom: none;
    }
  }

  .activity-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: $debtia-green;
    margin-top: 0.35rem;
    flex-shrink: 0;
    opacity: 0.6;
  }

  .activity-content {
    flex: 1;
    min-width: 0;
  }

  .activity-time {
    display: block;
    color: #8a9bb5;
    font-size: 0.75rem;
    margin-bottom: 0.15rem;
  }

  .activity-note {
    font-size: 0.82rem;
    color: #4a5568;
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;

    ::v-deep p {
      margin-bottom: 0 !important;
      display: inline;
    }
  }
}

// ==============================
// DOCUMENT LIST
// ==============================
.doc-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.doc-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.65rem 0.75rem;
  border-radius: 0.5rem;
  background: rgba(8, 140, 118, 0.03);
  border: 1px solid rgba(8, 140, 118, 0.08);
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(8, 140, 118, 0.07);
    border-color: rgba(8, 140, 118, 0.15);
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  }
}

.doc-type-badge {
  font-size: 0.65rem;
  padding: 0.2rem 0.45rem;
  min-width: 36px;
  text-align: center;
}

.doc-info {
  flex: 1;
  min-width: 0;

  .doc-title {
    display: block;
    font-weight: 400;
    font-size: 0.85rem;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .doc-meta {
    display: block;
    color: #8a9bb5;
    font-size: 0.75rem;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}

.doc-download-icon {
  flex-shrink: 0;
  color: $debtia-green;
  opacity: 0.5;
  transition: opacity 0.15s;

  .doc-item:hover & {
    opacity: 1;
  }
}

// ==============================
// MISC
// ==============================
.copy-btn {
  cursor: pointer;
  opacity: 0.5;
  transition: opacity 0.15s;
  &:hover {
    opacity: 1;
  }
}

.cursor-pointer {
  cursor: pointer;
}

.note-content ::v-deep p {
  margin-bottom: 0 !important;
}

// ==============================
// DEBTOR SKELETON
// ==============================
.debtor-skeleton {
  opacity: 1 !important;
  animation: none !important;

  .skeleton-avatar {
    width: 48px;
    height: 48px;
    border-radius: 8px;
    background: rgba(255, 255, 255, 0.1);
    margin: 0 auto;
    animation: skeletonShimmer 1.8s ease-in-out infinite;
  }

  .skeleton-line {
    border-radius: 4px;
    background: rgba(255, 255, 255, 0.1);
    animation: skeletonShimmer 1.8s ease-in-out infinite;
  }

  .skeleton-name {
    width: 60%;
    height: 14px;
    margin: 0.75rem auto 0;
  }

  .skeleton-type {
    width: 40%;
    height: 10px;
    margin: 0.5rem auto 0;
    animation-delay: 0.15s;
  }

  .skeleton-badge {
    width: 50px;
    height: 20px;
    border-radius: 10px;
    background: rgba(255, 255, 255, 0.08);
    margin: 0.5rem auto 0;
    animation: skeletonShimmer 1.8s ease-in-out infinite;
    animation-delay: 0.3s;
  }

  .skeleton-icon {
    width: 14px;
    height: 14px;
    border-radius: 3px;
    background: rgba(255, 255, 255, 0.08);
    flex-shrink: 0;
    margin-top: 0.15rem;
  }

  .skeleton-label {
    width: 30%;
    height: 8px;
    margin-bottom: 0.25rem;
    animation-delay: 0.1s;
  }

  .skeleton-value {
    width: 70%;
    height: 12px;
    animation-delay: 0.2s;
  }

  .info-row {
    display: flex;
    align-items: flex-start;
    padding: 0.6rem 0;
    border-bottom: 1px solid rgba(255, 255, 255, 0.08);
    gap: 0.6rem;

    &:last-child {
      border-bottom: none;
    }
  }
}

@keyframes skeletonShimmer {
  0%, 100% { opacity: 0.3; }
  50% { opacity: 0.6; }
}
</style>

<style lang="scss">
// Tab styling (unscoped so it applies to b-tabs internal elements)
.nav-pills-debtia {
  display: flex;
  align-items: center;
  gap: 0.5rem;

  .nav-link {
    border-radius: 0.75rem;
    padding: 0.5rem 1rem;
    font-weight: 400;
    transition: all 0.2s ease;
    display: flex;
    align-items: center;

    .doc-count-badge {
      background: rgba(8, 140, 118, 0.15);
      color: #088c76;
      font-size: 0.75rem;
      padding: 0.15rem 0.5rem;
      line-height: 1;
    }

    &.active {
      background: #088c76 !important;
      color: #fff !important;
      box-shadow: 0 4px 12px rgba(8, 140, 118, 0.4);

      .doc-count-badge {
        background: rgba(255, 255, 255, 0.25);
        color: #fff;
      }
    }
  }
}

.dark-layout .nav-pills-debtia {
  .nav-link {
    color: #8a9bb5;

    .doc-count-badge {
      background: rgba(8, 140, 118, 0.2);
      color: #4fd1c5;
    }

    &.active {
      background: #088c76 !important;
      color: #fff !important;

      .doc-count-badge {
        background: rgba(255, 255, 255, 0.25);
        color: #fff;
      }
    }
  }
}

.dark-layout .statements-card {
  background: rgba(10, 30, 50, 0.7);
  border: 1px solid rgba(8, 140, 118, 0.1);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.25);

  .statements-table {
    thead th {
      color: #5e7a94;
      border-bottom-color: rgba(8, 140, 118, 0.2);
    }

    tbody tr td {
      border-bottom-color: rgba(255, 255, 255, 0.04);
    }

    .st-row-alt td {
      background: rgba(8, 140, 118, 0.06);
    }

    tfoot .st-row-sum td {
      background: rgba(8, 140, 118, 0.12);
      border-top-color: rgba(8, 140, 118, 0.3);
    }

    .st-col-type {
      color: #4fd1c5;
    }
  }
}

.dark-layout .case-status-strip {
  background: rgba(10, 30, 50, 0.6);
  border: 1px solid rgba(8, 140, 118, 0.1);
  color: #a0b3c8;

  .strip-back-btn {
    background: rgba(8, 140, 118, 0.12);
    color: #4fd1c5;

    &:hover {
      background: rgba(8, 140, 118, 0.25);
    }
  }

  .strip-divider {
    background: rgba(255, 255, 255, 0.1);
  }
}

.dark-layout .status-reason-note {
  background: rgba(234, 84, 85, 0.1);
  border-color: rgba(234, 84, 85, 0.15);
  color: #e8a0a1;
}

.dark-layout .case-activity-footer {
  background: rgba(10, 30, 50, 0.6);
  border: 1px solid rgba(8, 140, 118, 0.1);

  .activity-item {
    border-bottom-color: rgba(255, 255, 255, 0.06);
  }

  .activity-note {
    color: #a0b3c8;
  }
}

.dark-layout .doc-item {
  background: rgba(8, 140, 118, 0.05);
  border-color: rgba(8, 140, 118, 0.1);

  &:hover {
    background: rgba(8, 140, 118, 0.1);
    border-color: rgba(8, 140, 118, 0.2);
  }

  .doc-title {
    color: #e2e8f0;
  }

  .doc-meta {
    color: #6b8299;
  }

  .doc-download-icon {
    color: #4fd1c5;
  }
}

.dark-layout .debtia-empty-state {
  border-color: rgba(8, 140, 118, 0.15);
  background: rgba(8, 140, 118, 0.03);

  .empty-state-icon {
    background: rgba(8, 140, 118, 0.12);
    color: #4fd1c5;
  }

  .empty-state-text {
    color: #6b8299;
  }
}

/* Case Details - consistent spacing matching --debtia-spacing */
#case-details .case-main-row {
  margin-left: calc(var(--debtia-spacing) / -2);
  margin-right: calc(var(--debtia-spacing) / -2);
  align-items: flex-start;
}

#case-details .case-main-row > [class*="col"] {
  padding-left: calc(var(--debtia-spacing) / 2) !important;
  padding-right: calc(var(--debtia-spacing) / 2) !important;
  margin-bottom: var(--debtia-spacing) !important;
}

/* Tabs card entrance animation */
#case-details .case-main-row > .order-lg-1 {
  animation: caseColFade 0.5s ease-out forwards;
  opacity: 0;
  animation-delay: 0.15s;
}

@keyframes caseColFade {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
.b-table-row-selected {
  outline: 2px solid var(--primary, #7367f0);
}
</style>
